import { useCallback, useRef } from 'react';

import {
   OnContinuouslyUsedMedicineSelection,
   OnMedicineSelection,
   PrescriptionIssuingPanelProps,
} from './PrescriptionIssuingPanel.types';
import { MedicineSearch } from './MedicineSelector/MedicineSearch';
import { OnMagistralDrugCreated } from './magistralDrug/create/MagistralDrugCreateForm.types';
import { PrescriptionBrowseTabs } from './PrescriptionBrowseTabs';
import { PrescriptionsApi } from './prescriptions/prescriptions.types';
import { PrescriptionsPanel } from './prescriptions/PrescriptionsPanel';
import { PrescriptionsPanelLoader } from './prescriptions/PrescriptionsPanel.loader';
import { StyledPrescriptionIssuingLeftPanel } from './StyledPrescriptionIssuingLeftPanel';
import { StyledPrescriptionIssuingPanel } from './StyledPrescriptionIssuingPanel';
import { StyledPrescriptionIssuingRightPanel } from './StyledPrescriptionIssuingRightPanel';

// eslint-disable-next-line max-lines-per-function
export const PrescriptionIssuingPanel = (props: PrescriptionIssuingPanelProps): JSX.Element => {
   const prescriptionsApiRef = useRef<PrescriptionsApi>(null);

   const handleMedicineSelection = useCallback<OnMedicineSelection>(
      (selectedPatientMedicine, selectedReimbursement) => {
         prescriptionsApiRef.current?.addDraftStandardPrescription(
            props.patientId,
            selectedPatientMedicine,
            selectedReimbursement
         );
      },
      [props.patientId]
   );

   const handleContinuouslyUsedMedicineSelection = useCallback<OnContinuouslyUsedMedicineSelection>(
      (selectedPatientMedicine, selectedPatientContinuouslyUsedMedicine) => {
         prescriptionsApiRef.current?.addDraftStandardContinuouslyUsedMedicinePrescription(
            props.patientId,
            selectedPatientMedicine,
            selectedPatientContinuouslyUsedMedicine
         );
      },
      [props.patientId]
   );

   const handleMagistralDrugCreated = useCallback<OnMagistralDrugCreated>(
      (magistralDrug) => {
         prescriptionsApiRef.current?.addDraftMagistralDrugPrescription(props.patientId, magistralDrug);
      },
      [props.patientId]
   );

   return (
      <StyledPrescriptionIssuingPanel>
         <StyledPrescriptionIssuingLeftPanel>
            <MedicineSearch
               patientId={props.patientId}
               visitDiagnoses={props.visitDiagnoses}
               onMedicineSelection={handleMedicineSelection}
               onMagistralDrugCreated={handleMagistralDrugCreated}
            >
               <PrescriptionBrowseTabs
                  patientId={props.patientId}
                  visitId={props.visitId}
                  visitDiagnoses={props.visitDiagnoses}
                  patientReferenceIdentifier={props.patientReferenceIdentifier}
                  locationId={props.locationId}
                  onMedicineSelection={handleMedicineSelection}
                  onContinuouslyUsedMedicineSelection={handleContinuouslyUsedMedicineSelection}
               />
            </MedicineSearch>
         </StyledPrescriptionIssuingLeftPanel>

         <StyledPrescriptionIssuingRightPanel>
            <PrescriptionsPanelLoader visitId={props.visitId}>
               {({ prescriptionPackages, setPrescriptionPackages }) => (
                  <PrescriptionsPanel
                     prescriptionsApiRef={prescriptionsApiRef}
                     visitId={props.visitId}
                     patientId={props.patientId}
                     patientAddress={props.patientAddress}
                     prescriptionPackages={prescriptionPackages}
                     onPrescriptionsSent={setPrescriptionPackages}
                     visitDiagnoses={props.visitDiagnoses}
                     inPersonExaminationPerformed={props.inPersonExaminationPerformed}
                     documentationScope={props.documentationScope}
                  />
               )}
            </PrescriptionsPanelLoader>
         </StyledPrescriptionIssuingRightPanel>
      </StyledPrescriptionIssuingPanel>
   );
};
